import { useMemo, useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Site, sitesApi } from '../../lib/sites';
import { brandingApi, type Branding } from '../../lib/branding';
import { organisationsApi, type Organisation } from '../../lib/organisations';
import { useAuth } from '../../hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TimePicker } from '@/components/ui/time-picker';
import { Textarea } from '@/components/ui/textarea';

const NONE_BRANDING = '__none__';
const NONE_ORGANISATION = '__none_org__';

interface SiteOverviewProps {
  site: Site;
  canEdit?: boolean;
  onError?: (message: string) => void;
}

export default function SiteOverview({ site, canEdit = false, onError }: SiteOverviewProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canConfigureCurfew = user?.role === 'super_user';
  const canConfigureOrganisation = user?.role === 'super_user';
  const canSeeCurfew = user?.role === 'super_user' || user?.role === 'site_admin';
  const [curfewError, setCurfewError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: site.name,
    description: site.description || '',
    organisationId: site.organisationId ?? null,
    brandingId: site.brandingId ?? null,
    curfewStart: site.curfewStart ?? '',
    curfewEnd: site.curfewEnd ?? '',
  });
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const hasChangesRef = useRef(false);
  const formDataRef = useRef(formData);
  const focusedFieldRef = useRef<string | null>(null);
  const justSavedRef = useRef(false);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Site>) => sitesApi.update(site.id, data),
    onSuccess: (updatedSite) => {
      const newFormData = {
        name: updatedSite.name,
        description: updatedSite.description || '',
        organisationId: updatedSite.organisationId ?? null,
        brandingId: updatedSite.brandingId ?? null,
        curfewStart: updatedSite.curfewStart ?? '',
        curfewEnd: updatedSite.curfewEnd ?? '',
      };

      formDataRef.current = newFormData;
      setFormData(newFormData);
      hasChangesRef.current = false;

      justSavedRef.current = true;

      queryClient.setQueryData(['site', site.id], updatedSite);

      queryClient.invalidateQueries({ queryKey: ['site', site.id] });

      setTimeout(() => {
        justSavedRef.current = false;
      }, 1000);

      if (focusedFieldRef.current) {
        setTimeout(() => {
          const input = document.getElementById(focusedFieldRef.current!);
          if (input) {
            input.focus();
          }
        }, 0);
      }
    },
    onError: (error: unknown) => {
      console.error('Error updating site:', error);
      const errorMessage =
        (error as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error ||
        (error as { message?: string })?.message ||
        'Failed to update site';
      onError?.(errorMessage);
    },
  });

  const { data: brandings, isLoading: brandingsLoading } = useQuery({
    queryKey: ['branding'],
    queryFn: () => brandingApi.getAll(),
    enabled: canEdit || !!site.brandingId,
  });

  const { data: organisations, isLoading: organisationsLoading } = useQuery({
    queryKey: ['organisations'],
    queryFn: () => organisationsApi.getAll(),
    enabled: canConfigureOrganisation || !!site.organisationId,
  });

  const brandingById = useMemo(() => {
    const map = new Map<string, Branding>();
    (brandings ?? []).forEach((b) => map.set(b.id, b));
    return map;
  }, [brandings]);

  const organisationById = useMemo(() => {
    const map = new Map<string, Organisation>();
    (organisations ?? []).forEach((o) => map.set(o.id, o));
    return map;
  }, [organisations]);

  const saveChanges = () => {
    const currentFormData = formDataRef.current;

    if (!hasChangesRef.current) {
      return;
    }

    const updateData: Partial<Site> = {
      name: currentFormData.name,
      description: currentFormData.description || undefined,
      brandingId: currentFormData.brandingId,
    };
    if (canConfigureOrganisation) {
      updateData.organisationId = currentFormData.organisationId;
    }
    if (canConfigureCurfew) {
      const startTrimmed = currentFormData.curfewStart.trim();
      const endTrimmed = currentFormData.curfewEnd.trim();
      const startSet = !!startTrimmed;
      const endSet = !!endTrimmed;
      if (startSet !== endSet) {
        setCurfewError('Either set both curfew times, or leave both empty.');
        return;
      }
      setCurfewError(null);
      updateData.curfewStart = currentFormData.curfewStart.trim()
        ? currentFormData.curfewStart.trim()
        : null;
      updateData.curfewEnd = currentFormData.curfewEnd.trim()
        ? currentFormData.curfewEnd.trim()
        : null;
    }

    updateMutation.mutate(updateData);
  };

  const debouncedSave = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      const currentFormData = formDataRef.current;

      if (hasChangesRef.current) {
        const updateData: Partial<Site> = {
          name: currentFormData.name,
          description: currentFormData.description || undefined,
          brandingId: currentFormData.brandingId,
        };
        if (canConfigureOrganisation) {
          updateData.organisationId = currentFormData.organisationId;
        }
        if (canConfigureCurfew) {
          const startTrimmed = currentFormData.curfewStart.trim();
          const endTrimmed = currentFormData.curfewEnd.trim();
          const startSet = !!startTrimmed;
          const endSet = !!endTrimmed;
          if (startSet !== endSet) {
            setCurfewError('Either set both curfew times, or leave both empty.');
            return;
          }
          setCurfewError(null);
          updateData.curfewStart = currentFormData.curfewStart.trim()
            ? currentFormData.curfewStart.trim()
            : null;
          updateData.curfewEnd = currentFormData.curfewEnd.trim()
            ? currentFormData.curfewEnd.trim()
            : null;
        }
        updateMutation.mutate(updateData);
      }

      if (focusedFieldRef.current) {
        setTimeout(() => {
          const input = document.getElementById(focusedFieldRef.current!);
          if (input) {
            input.focus();
          }
        }, 0);
      }
    }, 1000);
  };

  const handleFieldChange = (field: string, value: string) => {
    if ((field === 'curfewStart' || field === 'curfewEnd') && !canConfigureCurfew) {
      return;
    }

    if (!focusedFieldRef.current) {
      focusedFieldRef.current = field;
    }

    setFormData((prev) => {
      const newData = { ...prev, [field]: value };
      formDataRef.current = newData;
      return newData;
    });
    if (field === 'curfewStart' || field === 'curfewEnd') {
      setCurfewError(null);
    }

    hasChangesRef.current = true;

    if (canEdit) {
      debouncedSave();
    }

    if (focusedFieldRef.current === field) {
      setTimeout(() => {
        const input = document.getElementById(field);
        const activeElement = document.activeElement;
        if (input && activeElement !== input && activeElement !== document.body) {
          input.focus();
          if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) {
            const length = input.value.length;
            input.setSelectionRange(length, length);
          }
        }
      }, 0);
    }
  };

  const handleFieldBlur = (field: string) => {
    if (focusedFieldRef.current === field) {
      focusedFieldRef.current = null;
    }

    if (hasChangesRef.current) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = undefined;
      }
      saveChanges();
    }
  };

  const handleFieldFocus = (field: string) => {
    focusedFieldRef.current = field;
  };

  useEffect(() => {
    if (justSavedRef.current) {
      return;
    }

    if (!hasChangesRef.current) {
      const current = formDataRef.current;

      const hasChanged =
        site.name !== current.name ||
        (site.description || '') !== current.description ||
        (site.organisationId ?? null) !== current.organisationId ||
        (site.brandingId ?? null) !== current.brandingId ||
        (site.curfewStart ?? '') !== (current.curfewStart ?? '') ||
        (site.curfewEnd ?? '') !== (current.curfewEnd ?? '');

      if (hasChanged) {
        const newFormData = {
          name: site.name,
          description: site.description || '',
          organisationId: site.organisationId ?? null,
          brandingId: site.brandingId ?? null,
          curfewStart: site.curfewStart ?? '',
          curfewEnd: site.curfewEnd ?? '',
        };
        formDataRef.current = newFormData;
        setFormData(newFormData);
      }
    }
  }, [site]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              Site Information
            </CardTitle>
            {updateMutation.isPending && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            {canEdit ? (
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                onFocus={() => handleFieldFocus('name')}
                onBlur={() => handleFieldBlur('name')}
                required
                disabled={updateMutation.isPending}
              />
            ) : (
              <p className="text-lg font-semibold text-foreground">{site.name}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            {canEdit ? (
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                onFocus={() => handleFieldFocus('description')}
                onBlur={() => handleFieldBlur('description')}
                rows={3}
                disabled={updateMutation.isPending}
              />
            ) : (
              <p className="text-muted-foreground">{site.description || '-'}</p>
            )}
          </div>

          {(canConfigureOrganisation || site.organisationId) && (
            <div className="space-y-2 pt-4 border-t border-border">
              <Label htmlFor="site-organisation">Organisation</Label>
              {canConfigureOrganisation ? (
                <Select
                  value={(formData.organisationId ?? NONE_ORGANISATION) as string}
                  onValueChange={(v) => {
                    const nextOrganisationId = v === NONE_ORGANISATION ? null : v;
                    const newData = { ...formDataRef.current, organisationId: nextOrganisationId };
                    formDataRef.current = newData;
                    setFormData(newData);
                    hasChangesRef.current = true;
                    debouncedSave();
                  }}
                  disabled={updateMutation.isPending || organisationsLoading}
                >
                  <SelectTrigger id="site-organisation">
                    <SelectValue placeholder="Choose organisation…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_ORGANISATION}>None</SelectItem>
                    {(organisations ?? []).map((o) => (
                      <SelectItem key={o.id} value={o.id}>
                        {o.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {site.organisationId
                    ? organisationById.get(site.organisationId)?.name ?? 'Linked'
                    : 'Not set'}
                </p>
              )}
            </div>
          )}

          <div className="space-y-2 pt-4 border-t border-border">
            <Label htmlFor="site-branding">Branding</Label>
            {canEdit ? (
              <Select
                value={(formData.brandingId ?? NONE_BRANDING) as string}
                onValueChange={(v) => {
                  const nextBrandingId = v === NONE_BRANDING ? null : v;
                  const newData = { ...formDataRef.current, brandingId: nextBrandingId };
                  formDataRef.current = newData;
                  setFormData(newData);
                  hasChangesRef.current = true;
                  debouncedSave();
                }}
                disabled={updateMutation.isPending || brandingsLoading}
              >
                <SelectTrigger id="site-branding">
                  <SelectValue placeholder="Choose branding…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE_BRANDING}>None</SelectItem>
                  {(brandings ?? []).map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-sm text-muted-foreground">
                {site.brandingId
                  ? brandingById.get(site.brandingId)?.name ?? 'Linked'
                  : 'Not set'}
              </p>
            )}
          </div>

          {canSeeCurfew && (
            <div className="space-y-3 pt-4 border-t border-border">
              <div>
                <Label>Lighting curfew (local time)</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Optional daily window (24-hour local time). Super users can edit; site managers see this read-only.
                </p>
              </div>
              {canConfigureCurfew ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="curfewStart">Start</Label>
                    <TimePicker
                      id="curfewStart"
                      value={formData.curfewStart}
                      onChange={(v) => handleFieldChange('curfewStart', v)}
                      onFocus={() => handleFieldFocus('curfewStart')}
                      onBlur={() => handleFieldBlur('curfewStart')}
                      placeholder="Curfew start"
                      disabled={updateMutation.isPending}
                      minuteGranularity="minute"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="curfewEnd">End (Next day)</Label>
                    <TimePicker
                      id="curfewEnd"
                      value={formData.curfewEnd}
                      onChange={(v) => handleFieldChange('curfewEnd', v)}
                      onFocus={() => handleFieldFocus('curfewEnd')}
                      onBlur={() => handleFieldBlur('curfewEnd')}
                      placeholder="Curfew end"
                      disabled={updateMutation.isPending}
                      minuteGranularity="minute"
                    />
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {site.curfewStart || site.curfewEnd
                    ? `${site.curfewStart ?? '—'} – ${site.curfewEnd ?? '—'}`
                    : 'Not set'}
                </p>
              )}
              {canConfigureCurfew && curfewError && (
                <p className="text-xs text-destructive">{curfewError}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
